import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import {
  Target, Search, Plus, X, Filter as FilterIcon,
  ClipboardCheck, AlertTriangle, CheckCircle2, Clock, TrendingUp, Sparkles, Loader2,
} from "lucide-react";

const STATUSES = [
  { key: "not_started",       label: "Not Started",       tone: "slate" },
  { key: "in_progress",       label: "In Progress",       tone: "sky" },
  { key: "on_track",          label: "On Track",          tone: "emerald" },
  { key: "at_risk",           label: "At Risk",           tone: "amber" },
  { key: "achieved",          label: "Achieved",          tone: "emerald" },
  { key: "partially_achieved",label: "Partially Achieved",tone: "emerald" },
  { key: "not_achieved",      label: "Not Achieved",      tone: "rose" },
  { key: "paused",            label: "Paused",            tone: "slate" },
  { key: "closed",            label: "Closed",            tone: "slate" },
];

const TYPES = [
  { key: "achieve",         label: "Achieve" },
  { key: "maintain",        label: "Maintain" },
  { key: "reduce",          label: "Reduce" },
  { key: "develop",         label: "Develop" },
  { key: "prevent_manage",  label: "Prevent / Manage" },
  { key: "other",           label: "Other" },
];

const TONE_CLASS = {
  slate:   "border-slate-300 bg-slate-50 text-slate-700",
  sky:     "border-sky-300 bg-sky-50 text-sky-800",
  emerald: "border-emerald-300 bg-emerald-50 text-emerald-800",
  amber:   "border-amber-300 bg-amber-50 text-amber-800",
  rose:    "border-rose-300 bg-rose-50 text-rose-800",
};

function StatusBadge({ status }) {
  const s = STATUSES.find((x) => x.key === status);
  const label = s?.label || status || "—";
  const tone = s?.tone || "slate";
  return (
    <span className={`text-[10px] uppercase tracking-widest border rounded px-2 py-0.5 font-semibold ${TONE_CLASS[tone]}`}
      data-testid={`outcome-status-${status || "unknown"}`}>{label}</span>
  );
}

function SummaryCard({ testid, label, value, tone, active, onClick, icon: Icon }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`text-left border rounded-md p-4 transition ${active ? "border-emerald-500 shadow-[0_0_0_2px_rgba(16,185,129,0.15)]" : "border-slate-200 hover:border-slate-300"} bg-white`}>
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 ${tone}`} strokeWidth={2.2} />
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      </div>
      <div className="font-heading text-3xl font-semibold tabular-nums text-slate-900 mt-1">{value}</div>
    </button>
  );
}

export default function Outcomes() {
  const { user } = useAuth();
  const canCreate = ["admin", "manager", "senior_care_worker"].includes(user?.role);

  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ active: 0, on_track: 0, at_risk: 0, reviews_due: 0, achieved: 0 });
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState("");
  const [q, setQ] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [siteId, setSiteId] = useState("");
  const [keyWorkerId, setKeyWorkerId] = useState("");
  const [serviceUserId, setServiceUserId] = useState("");

  // Reference data
  const [categories, setCategories] = useState([]);
  const [sites, setSites] = useState([]);
  const [users, setUsers] = useState([]);
  const [serviceUsers, setServiceUsers] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        status: statusFilter || undefined,
        q: q || undefined,
        category_id: categoryId || undefined,
        site_id: siteId || undefined,
        key_worker_id: keyWorkerId || undefined,
        service_user_id: serviceUserId || undefined,
      };
      const r = await api.get("/outcomes", { params });
      setItems(r.data.items || []);
      setSummary(r.data.summary || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load outcomes");
    } finally { setLoading(false); }
  }, [statusFilter, q, categoryId, siteId, keyWorkerId, serviceUserId]);

  useEffect(() => { load(); }, [load]);

  // Reference data loaded once
  useEffect(() => {
    (async () => {
      try {
        const [c, s, u, su] = await Promise.all([
          api.get("/outcomes/categories"),
          api.get("/sites"),
          api.get("/auth/users"),
          api.get("/clients"),
        ]);
        setCategories(c.data.items || []);
        setSites(s.data || []);
        setUsers(u.data || []);
        setServiceUsers((su.data || []).filter((x) => x?.id));
      } catch { /* non-fatal */ }
    })();
  }, []);

  const activeFilter = statusFilter;

  const cards = [
    { key: "",            label: "Active Outcomes", value: summary.active,      tone: "text-slate-600",   icon: Target,          testid: "kpi-active" },
    { key: "on_track",    label: "On Track",         value: summary.on_track,    tone: "text-emerald-600", icon: TrendingUp,      testid: "kpi-on-track" },
    { key: "at_risk",     label: "At Risk",          value: summary.at_risk,     tone: "text-amber-600",   icon: AlertTriangle,   testid: "kpi-at-risk" },
    { key: "__reviews",   label: "Reviews Due",      value: summary.reviews_due, tone: "text-sky-600",     icon: Clock,           testid: "kpi-reviews-due" },
    { key: "achieved",    label: "Achieved",         value: summary.achieved,    tone: "text-emerald-600", icon: CheckCircle2,    testid: "kpi-achieved" },
  ];

  return (
    <div className="p-4 lg:p-6 max-w-[1400px] mx-auto" data-testid="outcomes-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight text-slate-900">Outcomes</h1>
          <p className="text-sm text-slate-500 mt-1">Track person-centred goals, progress and evidence of meaningful outcomes.</p>
        </div>
        {canCreate && (
          <Button onClick={() => setShowCreate(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-create-outcome">
            <Plus className="w-4 h-4 mr-1.5" /> Create Outcome
          </Button>
        )}
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {cards.map((c) => (
          <SummaryCard key={c.testid} {...c}
            active={c.key === "__reviews"
              ? statusFilter === ""  // reviews_due filter isn't a status
              : activeFilter === c.key}
            onClick={() => setStatusFilter(c.key === "__reviews" ? "" : (activeFilter === c.key ? "" : c.key))} />
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-md p-3 mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
            <FilterIcon className="w-3.5 h-3.5" /> Filters
          </div>
          <div className="relative w-full sm:w-auto">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search outcomes…"
              className="pl-7 pr-2 h-11 sm:h-8 text-sm border border-slate-300 rounded-md w-full sm:w-56" data-testid="outcomes-search" />
          </div>
          <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}
            className="h-11 sm:h-8 text-sm border border-slate-300 rounded-md px-2 flex-1 sm:flex-none min-w-[45%] sm:min-w-0" data-testid="filter-category">
            <option value="">All categories</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="h-11 sm:h-8 text-sm border border-slate-300 rounded-md px-2 flex-1 sm:flex-none min-w-[45%] sm:min-w-0" data-testid="filter-status">
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <select value={siteId} onChange={(e) => setSiteId(e.target.value)}
            className="h-11 sm:h-8 text-sm border border-slate-300 rounded-md px-2 flex-1 sm:flex-none min-w-[45%] sm:min-w-0" data-testid="filter-site">
            <option value="">All sites</option>
            {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select value={serviceUserId} onChange={(e) => setServiceUserId(e.target.value)}
            className="h-11 sm:h-8 text-sm border border-slate-300 rounded-md px-2 flex-1 sm:flex-none min-w-[45%] sm:min-w-0" data-testid="filter-service-user">
            <option value="">All service users</option>
            {serviceUsers.map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}
          </select>
          <select value={keyWorkerId} onChange={(e) => setKeyWorkerId(e.target.value)}
            className="h-11 sm:h-8 text-sm border border-slate-300 rounded-md px-2 flex-1 sm:flex-none min-w-[45%] sm:min-w-0" data-testid="filter-key-worker">
            <option value="">All key workers</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          {(q || statusFilter || categoryId || siteId || keyWorkerId || serviceUserId) && (
            <button onClick={() => { setQ(""); setStatusFilter(""); setCategoryId(""); setSiteId(""); setKeyWorkerId(""); setServiceUserId(""); }}
              className="text-[11px] text-slate-500 hover:text-slate-800 ml-1" data-testid="filter-clear">Clear</button>
          )}
          {serviceUserId && (
            <button
              onClick={() => setAiOpen(true)}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-semibold text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 ml-auto"
              data-testid="btn-outcomes-ai-insights"
            >
              <Sparkles className="w-3.5 h-3.5" /> AI Insights
            </button>
          )}
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-sm text-slate-500 py-6" data-testid="outcomes-loading">Loading outcomes…</div>
      ) : items.length === 0 ? (
        <EmptyState canCreate={canCreate} onCreate={() => setShowCreate(true)} />
      ) : (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="outcomes-list">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200 bg-slate-50">
                <th className="py-2 px-3">Service User</th>
                <th className="py-2 px-3">Outcome</th>
                <th className="py-2 px-3">Category</th>
                <th className="py-2 px-3">Progress</th>
                <th className="py-2 px-3">Status</th>
                <th className="py-2 px-3">Target</th>
                <th className="py-2 px-3">Next Review</th>
                <th className="py-2 px-3">Key Worker</th>
              </tr>
            </thead>
            <tbody>
              {items.map((o) => (
                <tr key={o.id} className="border-b border-slate-100 align-top hover:bg-slate-50" data-testid={`outcome-row-${o.id}`}>
                  <td className="py-2 px-3 whitespace-nowrap">
                    <Link to={`/clients/${o.service_user_id}`} className="text-emerald-700 hover:underline">{o.service_user_name || "—"}</Link>
                  </td>
                  <td className="py-2 px-3">
                    <Link to={`/outcomes/${o.id}`} className="font-medium text-slate-900 hover:text-emerald-700" data-testid={`outcome-title-${o.id}`}>{o.title}</Link>
                    {o.person_statement && <div className="text-[11px] text-slate-500 italic mt-0.5 line-clamp-1">“{o.person_statement}”</div>}
                  </td>
                  <td className="py-2 px-3 text-slate-700">{o.category_name || "—"}</td>
                  <td className="py-2 px-3">
                    {typeof o.progress_value === "number" ? (
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-emerald-500" style={{ width: `${o.progress_value}%` }} />
                        </div>
                        <span className="text-[11px] tabular-nums text-slate-600">{o.progress_value}%</span>
                      </div>
                    ) : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="py-2 px-3"><StatusBadge status={o.status} /></td>
                  <td className="py-2 px-3 text-slate-700 tabular-nums">{o.target_date || "—"}</td>
                  <td className="py-2 px-3 text-slate-700 tabular-nums">{o.next_review_date || "—"}</td>
                  <td className="py-2 px-3 text-slate-700">{o.key_worker_name || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <CreateOutcomeModal
          onClose={() => setShowCreate(false)}
          onCreated={async () => { setShowCreate(false); await load(); toast.success("Outcome created"); }}          categories={categories}
          serviceUsers={serviceUsers}
          users={users}
        />
      )}
      {aiOpen && serviceUserId && (
        <AiInsightsModal
          clientId={serviceUserId}
          serviceUserName={serviceUsers.find((s) => s.id === serviceUserId)?.full_name}
          onClose={() => setAiOpen(false)}
        />
      )}
    </div>
  );
}

function AiInsightsModal({ clientId, serviceUserName, onClose }) {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = async (refresh = false) => {
    (refresh ? setRefreshing : setLoading)(true);
    setError(null);
    try {
      const r = await api.post(`/outcomes/service-user/${clientId}/ai-insights${refresh ? "?refresh=true" : ""}`);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not generate insights");
    } finally { setLoading(false); setRefreshing(false); }
  };
  useEffect(() => { load(false); }, [clientId]);

  const priorityTone = {
    high:   "bg-rose-50 border-rose-200 text-rose-900",
    medium: "bg-amber-50 border-amber-200 text-amber-900",
    low:    "bg-slate-50 border-slate-200 text-slate-700",
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[86vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="ai-insights-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-gradient-to-r from-emerald-50 to-teal-50">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-800 font-semibold">AI Outcome Insights</div>
            <div className="font-heading text-lg font-semibold text-slate-900">{serviceUserName || "Service user"}</div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => load(true)}
              disabled={loading || refreshing}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-semibold text-slate-700 border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50"
              data-testid="ai-insights-refresh"
            >
              {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Regenerate
            </button>
            <button onClick={onClose} className="p-1 text-slate-500 hover:text-slate-800" data-testid="ai-insights-close" aria-label="Close"><X className="w-4 h-4" /></button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4 text-sm">
          {loading ? (
            <div className="py-10 text-center text-slate-500" data-testid="ai-insights-loading">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-emerald-600" />
              Analysing outcomes evidence…
            </div>
          ) : error ? (
            <div className="border border-rose-200 bg-rose-50 rounded-md p-3 text-rose-800" data-testid="ai-insights-error">{error}</div>
          ) : data && (
            <>
              <section>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Summary</div>
                <p className="text-slate-800 leading-relaxed whitespace-pre-wrap" data-testid="ai-insights-summary">{data.summary}</p>
                <div className="text-[10px] text-slate-500 mt-2">
                  {data.outcome_count} outcome{data.outcome_count === 1 ? "" : "s"} analysed
                  {data.cached ? " · cached" : ""} · generated {data.generated_at ? new Date(data.generated_at).toLocaleString() : "—"}
                </div>
              </section>
              {data.strengths?.length > 0 && (
                <section data-testid="ai-insights-strengths">
                  <div className="text-[10px] uppercase tracking-widest text-emerald-800 font-semibold mb-1">Strengths</div>
                  <ul className="list-disc list-inside space-y-1 text-slate-800">
                    {data.strengths.map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                </section>
              )}
              {data.concerns?.length > 0 && (
                <section data-testid="ai-insights-concerns">
                  <div className="text-[10px] uppercase tracking-widest text-amber-800 font-semibold mb-1">Concerns</div>
                  <ul className="list-disc list-inside space-y-1 text-slate-800">
                    {data.concerns.map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                </section>
              )}
              {data.recommendations?.length > 0 && (
                <section data-testid="ai-insights-recommendations">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Recommendations</div>
                  <ul className="space-y-2">
                    {data.recommendations.map((r, i) => (
                      <li key={i} className={`border rounded-md p-2 ${priorityTone[r.priority] || priorityTone.medium}`}>
                        <div className="text-sm font-semibold">{r.title}</div>
                        {r.why && <div className="text-xs mt-0.5 opacity-90">{r.why}</div>}
                        <div className="text-[10px] mt-1 uppercase tracking-widest opacity-70">
                          {r.owner_role || "manager"} · {r.priority || "medium"} priority
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 text-[11px] text-slate-500">
          AI-generated from recorded outcomes, progress and reviews. Facts are not invented — verify before acting. Not a clinical/regulatory decision.
        </div>
      </div>
    </div>
  );
}

function EmptyState({ canCreate, onCreate }) {
  return (
    <div className="bg-white border border-dashed border-slate-300 rounded-md p-10 text-center" data-testid="outcomes-empty">
      <ClipboardCheck className="w-8 h-8 text-slate-400 mx-auto mb-3" />
      <div className="font-heading text-lg font-semibold text-slate-900">No Outcomes Yet</div>
      <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
        Create person-centred outcomes to track what matters to each person and how their support is helping them make progress.
      </p>
      {canCreate && (
        <Button onClick={onCreate} className="bg-emerald-600 hover:bg-emerald-700 mt-4" data-testid="btn-create-first-outcome">
          <Plus className="w-4 h-4 mr-1.5" /> Create First Outcome
        </Button>
      )}
    </div>
  );
}

function CreateOutcomeModal({ onClose, onCreated, categories, serviceUsers, users }) {
  const [saving, setSaving] = useState(false);
  const [carePlans, setCarePlans] = useState([]);
  const [form, setForm] = useState({
    service_user_id: "",
    category_id: "",
    title: "",
    person_statement: "",
    desired_outcome: "",
    success_measure: "",
    outcome_type: "achieve",
    support_strategy: "",
    status: "not_started",
    key_worker_id: "",
    care_plan_id: "",
    start_date: new Date().toISOString().slice(0, 10),
    target_date: "",
    next_review_date: "",
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  // Load care plans when a service user is picked
  useEffect(() => {
    if (!form.service_user_id) { setCarePlans([]); return; }
    (async () => {
      try {
        const r = await api.get(`/clients/${form.service_user_id}/sections/care-planning/records`);
        setCarePlans(r.data || []);
      } catch { setCarePlans([]); }
    })();
  }, [form.service_user_id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service_user_id || !form.category_id || !form.title.trim()) {
      toast.error("Service user, category and title are required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      // Strip empty strings so backend defaults kick in
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") delete payload[k];
      });
      await api.post("/outcomes", payload);
      await onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create outcome");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-6 overflow-auto" data-testid="create-outcome-modal">
      <div className="bg-white rounded-md shadow-xl w-full max-w-3xl">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Create Outcome</div>
            <div className="text-sm text-slate-600 mt-0.5">Capture what matters to the person and how progress will be measured.</div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-800" data-testid="btn-close-create-outcome"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Service User *">
            <select required value={form.service_user_id} onChange={set("service_user_id")}
              className="w-full h-9 px-2 border border-slate-300 rounded-md text-sm" data-testid="create-service-user">
              <option value="">— Pick service user —</option>
              {serviceUsers.map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}
            </select>
          </Field>
          <Field label="Category *">
            <select required value={form.category_id} onChange={set("category_id")}
              className="w-full h-9 px-2 border border-slate-300 rounded-md text-sm" data-testid="create-category">
              <option value="">— Pick category —</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Outcome Title *" full>
            <input required value={form.title} onChange={set("title")} placeholder="e.g. Independent Breakfast Preparation"
              className="w-full h-9 px-2 border border-slate-300 rounded-md text-sm" data-testid="create-title" />
          </Field>
          <Field label="What matters to me? (person's own words)" full>
            <textarea rows={2} value={form.person_statement} onChange={set("person_statement")}
              placeholder='"I want to be able to make my own breakfast without staff doing it for me."'
              className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm" data-testid="create-person-statement" />
          </Field>
          <Field label="Desired outcome" full>
            <textarea rows={2} value={form.desired_outcome} onChange={set("desired_outcome")}
              placeholder="e.g. Bayo will prepare his own breakfast safely with minimal or no staff prompting."
              className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm" data-testid="create-desired-outcome" />
          </Field>
          <Field label="Success measure" full>
            <textarea rows={2} value={form.success_measure} onChange={set("success_measure")}
              placeholder="e.g. Prepare breakfast independently at least 4 days per week for four consecutive weeks."
              className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm" data-testid="create-success-measure" />
          </Field>
          <Field label="How staff should support me" full>
            <textarea rows={2} value={form.support_strategy} onChange={set("support_strategy")}
              placeholder="e.g. Staff should encourage Bayo to prepare breakfast himself; provide verbal prompts only when required."
              className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm" data-testid="create-support-strategy" />
          </Field>
          <Field label="Outcome type">
            <select value={form.outcome_type} onChange={set("outcome_type")}
              className="w-full h-9 px-2 border border-slate-300 rounded-md text-sm" data-testid="create-outcome-type">
              {TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </Field>
          <Field label="Status">
            <select value={form.status} onChange={set("status")}
              className="w-full h-9 px-2 border border-slate-300 rounded-md text-sm" data-testid="create-status">
              {STATUSES.filter((s) => !["achieved", "closed"].includes(s.key)).map((s) => (
                <option key={s.key} value={s.key}>{s.label}</option>
              ))}
            </select>
          </Field>
          <Field label="Key worker">
            <select value={form.key_worker_id} onChange={set("key_worker_id")}
              className="w-full h-9 px-2 border border-slate-300 rounded-md text-sm" data-testid="create-key-worker">
              <option value="">— None —</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
            </select>
          </Field>
          <Field label="Link to Care Plan">
            <select value={form.care_plan_id} onChange={set("care_plan_id")}
              disabled={!form.service_user_id}
              className="w-full h-9 px-2 border border-slate-300 rounded-md text-sm disabled:bg-slate-50" data-testid="create-care-plan">
              <option value="">{form.service_user_id ? "— None —" : "Pick service user first"}</option>
              {carePlans.map((cp) => (
                <option key={cp.id} value={cp.id}>{cp?.data?.title || cp?.data?.plan_type || "Care Plan"}</option>
              ))}
            </select>
          </Field>
          <Field label="Start date">
            <input type="date" value={form.start_date} onChange={set("start_date")}
              className="w-full h-9 px-2 border border-slate-300 rounded-md text-sm" data-testid="create-start-date" />
          </Field>
          <Field label="Target date">
            <input type="date" value={form.target_date} onChange={set("target_date")}
              className="w-full h-9 px-2 border border-slate-300 rounded-md text-sm" data-testid="create-target-date" />
          </Field>
          <Field label="Next review date">
            <input type="date" value={form.next_review_date} onChange={set("next_review_date")}
              className="w-full h-9 px-2 border border-slate-300 rounded-md text-sm" data-testid="create-next-review" />
          </Field>

          <div className="col-span-full flex items-center justify-end gap-2 pt-2 border-t border-slate-100 mt-2">
            <button type="button" onClick={onClose} className="text-sm text-slate-500 hover:text-slate-800">Cancel</button>
            <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-submit-outcome">
              {saving ? "Creating…" : "Create Outcome"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, full, children }) {
  return (
    <label className={`text-[11px] text-slate-600 ${full ? "md:col-span-2" : ""}`}>
      {label}
      <div className="mt-1">{children}</div>
    </label>
  );
}
